import { create } from 'zustand';
import { TemplateVariableDefinitionDto } from '@/features/dashboard/messageTemplates/types/TemplateVariableDefinitionDto.ts';

type Command = (item: TemplateVariableDefinitionDto) => void;

interface SuggestionStore {
  open: boolean;
  items: TemplateVariableDefinitionDto[];
  clientRect: DOMRect | null;
  selectedIndex: number;
  command: Command | null;

  show: (props: {
    items: TemplateVariableDefinitionDto[];
    command: Command;
    clientRect: DOMRect | null;
  }) => void;
  update: (props: {
    items: TemplateVariableDefinitionDto[];
    command: Command;
    clientRect: DOMRect | null;
  }) => void;
  hide: () => void;
  setSelectedIndex: (i: number) => void;
  select: (index: number) => void;
  handleKey: (event: KeyboardEvent) => boolean;
}

// Bridges the `@tiptap/suggestion` plugin (which runs in ProseMirror, outside
// React) with the React-rendered `SuggestionPopup`. The plugin's lifecycle
// callbacks fire synchronously from ProseMirror transactions, where React
// state isn't reachable; they push into this store, and the popup component
// subscribes to render reactively.
//
// Single instance — only one suggestion can be active at a time across all
// editors, so a module-scope store is fine (no need to scope per editor).
export const useSuggestionStore = create<SuggestionStore>((set, get) => ({
  open: false,
  items: [],
  clientRect: null,
  selectedIndex: 0,
  command: null,

  show: ({ items, command, clientRect }) =>
    set({ open: true, items, command, clientRect, selectedIndex: 0 }),

  update: ({ items, command, clientRect }) =>
    set({ items, command, clientRect, selectedIndex: 0 }),

  hide: () =>
    set({ open: false, items: [], command: null, clientRect: null }),

  setSelectedIndex: (i) => set({ selectedIndex: i }),

  select: (index) => {
    const { items, command } = get();
    if (items[index] && command) command(items[index]);
  },

  handleKey: (event) => {
    const { open, items, selectedIndex } = get();
    if (!open || items.length === 0) return false;
    if (event.key === 'ArrowDown') {
      set({ selectedIndex: (selectedIndex + 1) % items.length });
      return true;
    }
    if (event.key === 'ArrowUp') {
      set({ selectedIndex: (selectedIndex + items.length - 1) % items.length });
      return true;
    }
    if (event.key === 'Enter') {
      get().select(selectedIndex);
      return true;
    }
    return false;
  },
}));
